"use client";

import { Check, ChevronDown, PanelsTopLeft } from "lucide-react";
import type { KeyboardEvent, ReactNode } from "react";
import type {
  BuilderNavigationSelectControl,
  BuilderNavigationVariantOption,
  BuilderNavigationVariantPickerAction,
} from "./navigationVariantPickerModel";

function selectRadioFromKeyboard(
  event: KeyboardEvent<HTMLDivElement>,
  onSelect: () => void,
): void {
  if (event.key !== "Enter" && event.key !== " ") return;

  event.preventDefault();
  onSelect();
}

export function BuilderNavigationVariantPicker<Value extends string>({
  actions = [],
  actionsLabel,
  badge,
  controls = [],
  description,
  label,
  onChange,
  options,
  renderPreview,
  settingsLabel,
  value,
}: {
  actions?: readonly BuilderNavigationVariantPickerAction[];
  actionsLabel?: string;
  badge: string;
  controls?: readonly BuilderNavigationSelectControl[];
  description: string;
  label: string;
  onChange: (value: Value) => void;
  options: readonly BuilderNavigationVariantOption<Value>[];
  renderPreview: (value: Value) => ReactNode;
  settingsLabel?: string;
  value: Value;
}) {
  const activeOption = options.find((option) => option.value === value);

  return (
    <section
      className="builder-inspector-section"
      data-builder-inspector-settings-section="navigation-layout"
      data-builder-navigation-layout-picker="true"
    >
      <div className="flex min-h-12 items-center gap-3 px-3.5 py-3 text-text">
        <PanelsTopLeft aria-hidden="true" className="h-[18px] w-[18px] shrink-0" strokeWidth={1.65} />
        <span className="min-w-0 flex-1 text-[13px] font-medium">
          {label}
        </span>
        <span className="rounded-full border border-primary/20 bg-primary/10 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wide text-primary">
          {badge}
        </span>
      </div>
      <div
        className="builder-inspector-section-content grid gap-3"
        title={description}
      >
        <div
          aria-label={label}
          className="grid grid-cols-2 gap-1.5"
          data-builder-navigation-variant-options="true"
          role="radiogroup"
        >
          {options.map((option) => {
            const active = option.value === value;

            return (
              <div
                aria-checked={active}
                aria-label={`${option.label}. ${option.description}`}
                className={[
                  "group relative min-w-0 cursor-pointer rounded-md border p-1 text-left outline-none transition focus-visible:ring-2 focus-visible:ring-primary",
                  active
                    ? "border-primary bg-primary/10 ring-1 ring-primary/15"
                    : "border-border bg-surface hover:border-border-strong hover:bg-surface",
                ].join(" ")}
                data-builder-navigation-variant-option={option.value}
                key={option.value}
                onClick={() => onChange(option.value)}
                onKeyDown={(event) => selectRadioFromKeyboard(
                  event,
                  () => onChange(option.value),
                )}
                role="radio"
                tabIndex={0}
                title={option.description}
              >
                <div
                  aria-hidden="true"
                  className="pointer-events-none relative h-20 overflow-hidden rounded border border-border bg-[#161821] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.025)]"
                  data-builder-navigation-production-preview={option.value}
                  inert
                >
                  {renderPreview(option.value)}
                </div>
                <span className="mt-1 flex min-w-0 items-center gap-1">
                  <span className="min-w-0 flex-1 truncate text-[9px] font-semibold leading-3.5 text-text">
                    {option.label}
                  </span>
                  {active ? (
                    <span className="grid h-4 w-4 shrink-0 place-items-center rounded-full bg-primary text-[#172027]">
                      <Check aria-hidden="true" className="h-2.5 w-2.5" />
                    </span>
                  ) : null}
                </span>
              </div>
            );
          })}
        </div>
        {activeOption ? (
          <p
            className="text-[9px] leading-3.5 text-text-secondary"
            data-builder-navigation-active-description="true"
          >
            {activeOption.description}
          </p>
        ) : null}
        {controls.length > 0 ? (
          <details
            className="group/settings border-t border-border pt-2"
            data-builder-navigation-settings="true"
          >
            <summary className="flex cursor-pointer list-none items-center gap-2 text-[10px] font-semibold text-text-secondary marker:hidden">
              <span className="min-w-0 flex-1 truncate">
                {settingsLabel ?? label}
              </span>
              <ChevronDown
                aria-hidden="true"
                className="h-3.5 w-3.5 shrink-0 text-text-secondary transition-transform group-open/settings:rotate-180"
              />
            </summary>
            <fieldset className="mt-2 grid grid-cols-2 gap-2">
              {controls.map((control) => (
                <label className="grid min-w-0 gap-1" key={control.key}>
                  <span className="truncate text-[9px] font-medium text-text-secondary">
                    {control.label}
                  </span>
                  <select
                    className="h-8 min-w-0 rounded-md border border-border bg-surface px-2 text-[10px] text-text outline-none transition focus:border-primary"
                    data-builder-navigation-setting={control.key}
                    onChange={(event) => control.onChange(event.target.value)}
                    value={control.value}
                  >
                    {control.options.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
            </fieldset>
          </details>
        ) : null}
        {actions.length > 0 ? (
          <div className="grid gap-1.5 border-t border-border pt-3">
            {actionsLabel ? (
              <span className="text-[10px] font-semibold text-text-secondary">
                {actionsLabel}
              </span>
            ) : null}
            <div className="grid grid-cols-2 gap-1.5">
              {actions.map((action) => (
                <button
                  className="flex min-h-8 min-w-0 items-center justify-center rounded-md border border-border bg-surface px-2 py-1.5 text-[9px] font-semibold text-text transition hover:border-primary/50 hover:bg-surface disabled:cursor-not-allowed disabled:opacity-45"
                  data-builder-navigation-action={action.id}
                  disabled={action.disabled}
                  key={action.id}
                  onClick={() => void action.onSelect()}
                  type="button"
                >
                  <span className="truncate">{action.label}</span>
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}
